<script setup lang="ts">
import { getFilterDtwinRangeOptionValue } from "../utils/getFilterDtwinRangeOptionValue";
import { getFilterDtwinRangeValueFromRangeOptionValue } from "../utils/getFilterDtwinRangeValueFromRangeOptionValue";
import { useSerialize, type UniqueLabel } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { SearchMode } from "~/core/composables/useSearch";
import { DISTINCT_VALUES_KEY } from "~/core/constants/constants";
import filtersUtils from "~/core/utils/filters.utils";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import type { DtwinConnectivityStatus } from "~/modules/dtwin/models/DtwinConnectivityStatus.enum";
import type { DtwinRange } from "~/modules/dtwin/models/DtwinRange.interface";
import type {
  DtwinTypeFilterOptions,
  DtwinTypeOption,
} from "~/modules/dtwin/models/DtwinTypeOptions.interface";
import { getFilterDtwinTypeOptionValue } from "~/modules/dtwin/utils/getFilterDtwinTypeOptionValue";
import { getFilterDtwinTypeValueFromRangeOptionValue } from "~/modules/dtwin/utils/getFilterDtwinTypeValueFromRangeOptionValue";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type {
  FilterFormSection,
  FilterOptions,
  FilterSelectionValue,
  FiltersSelection,
  Option,
} from "~/types";
import { FilterInputType, Filters } from "~/types";

const props = defineProps<{
  organizationCode?: number;
  filters: FiltersSelection;
  typeOptions?: DtwinTypeFilterOptions;
  lifecycleStatesOptions?: FilterOptions;
  disableSectionDeviceType?: boolean;
  disableSectionLifecycleState?: boolean;
  disableSectionConnectivity?: boolean;
}>();

const emit = defineEmits(["change", "enter"]);

const { t } = useI18n();

const serialize = useSerialize();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  isLoading: isLoadingRange,
  results: resultsRange,
  error: errorRange,
  fetchData: fetchDataRange,
} = useFetchDtwins({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
  useQueryParametersForPagination: false,
});

const DISTINCT_VALUES_RANGE = "deviceModelName,attributes.range";

const connectivityTypes: Ref<DtwinConnectivityStatus[]> = ref([]);
const appliedConnectivityTypes: Ref<DtwinConnectivityStatus[]> = ref([]);
const types: Ref<string[]> = ref([]);
const appliedTypes: Ref<string[]> = ref([]);
const ranges: Ref<string[]> = ref([]);
const appliedRanges: Ref<string[]> = ref([]);

const deviceTypeLabel = ref(t("dtwins.filters.deviceType"));
const lifecycleLabel = ref(t("dtwins.filters.lifecycleState"));
const connectivityLabel = ref(t("dtwins.filters.connectivity.label"));
const rangesLabel = ref(t("dtwins.filters.ranges.label"));

const filterTypeOptions: ComputedRef<DtwinTypeOption[]> = computed(() => {
  return (props.typeOptions?.options ?? []).map((typeOption) => {
    const newTypeOption = { ...typeOption };
    if (
      !isLoadingRange.value &&
      !errorRange.value &&
      resultsRange.value?.distinctValues &&
      resultsRange.value.distinctValues[DISTINCT_VALUES_RANGE]
    ) {
      newTypeOption.ranges = resultsRange.value.distinctValues[
        DISTINCT_VALUES_RANGE
      ].reduce((accumulator: Option[], currentValue: DtwinRange) => {
        if (
          currentValue.deviceModelName === typeOption.name &&
          currentValue["attributes.range"]
        ) {
          accumulator.push({
            label: currentValue["attributes.range"],
            value: currentValue["attributes.range"],
            disabled: typeOption.disabled,
          });
        }
        return accumulator;
      }, []);
    }
    return newTypeOption;
  });
});

const selectedFiltersByTypes = computed(() => [
  connectivityTypes.value,
  types.value,
  ranges.value,
]);

const selectedFilterTags = computed((): UniqueLabel[] => {
  return buildFilterTags(connectivityTypes.value, types.value, ranges.value);
});

const appliedFilterTags = computed((): UniqueLabel[] => {
  const tags = buildFilterTags(
    appliedConnectivityTypes.value,
    appliedTypes.value,
    appliedRanges.value,
  );
  return tags;
});

const filterFormSections: ComputedRef<FilterFormSection[]> = computed(() => {
  const result: FilterFormSection[] = [];

  result.push({
    disabled: props.disableSectionDeviceType,
    filter: Filters.DTWIN_MODEL_TYPE,
    footerId: "deviceTypes-footer",
    footerEnabled: true,
    header: deviceTypeLabel.value,
    id: "device-types",
    inputType: FilterInputType.CUSTOM,
    menuLabel: deviceTypeLabel.value,
    tagPrefix: deviceTypeLabel.value,
    additionalFilter: Filters.DTWIN_RANGE,
  });

  result.push({
    disabled: props.disableSectionLifecycleState,
    filter: Filters.DTWINS_IN_LIFE_CYCLE_STATE,
    header: lifecycleLabel.value,
    footerId: "lifecycleStates-footer",
    footerEnabled: true,
    id: "lifecycleStates",
    inputType: FilterInputType.CHECKBOX,
    menuLabel: lifecycleLabel.value,
    options: computed(() => props.lifecycleStatesOptions?.options ?? []),
    tagPrefix: lifecycleLabel.value,
    translate: false,
  });

  result.push({
    disabled: props.disableSectionConnectivity,
    filter: Filters.DTWIN_CONNECTIVITY_STATUS,
    footerEnabled: true,
    footerId: "connectivity-footer",
    header: connectivityLabel.value,
    id: "connectivity",
    inputType: FilterInputType.CUSTOM,
    menuLabel: connectivityLabel.value,
    tagPrefix: connectivityLabel.value,
  });

  return result;
});

function setFilter(filter: Filters, value: FilterSelectionValue) {
  emit("change", filter, value);
}

function applyFilter() {
  emit("change", Filters.DTWIN_CONNECTIVITY_STATUS, connectivityTypes.value);

  emit("change", Filters.DTWIN_MODEL_TYPE, types.value);

  emit(
    "change",
    Filters.DTWIN_RANGE,
    ranges.value.map((range) =>
      getFilterDtwinRangeValueFromRangeOptionValue(range),
    ),
  );

  emit("enter");
}

function onAppliedFilterChange() {
  appliedConnectivityTypes.value = connectivityTypes.value;
  appliedTypes.value = types.value;
  appliedRanges.value = ranges.value;
}

function clearFilter() {
  connectivityTypes.value = [];

  // keep disabled device type options
  types.value = types.value.filter((deviceType) => {
    const option = props.typeOptions?.options?.find((currentOption) => {
      return currentOption.value.includes(deviceType);
    });
    return option && option.disabled;
  });

  // keep disabled range options
  ranges.value = ranges.value.filter((range) => {
    const deviceTypeOption = filterTypeOptions.value.find(
      (currentOption) =>
        getFilterDtwinTypeOptionValue(currentOption) ===
        getFilterDtwinTypeValueFromRangeOptionValue(range),
    );

    const rangeOption = deviceTypeOption?.ranges?.find(
      (rangeOption) =>
        rangeOption.value ===
        getFilterDtwinRangeValueFromRangeOptionValue(range),
    );

    return !rangeOption || rangeOption.disabled;
  });

  applyFilter();
}

function buildFilterTags(
  connectivityTypes: DtwinConnectivityStatus[],
  deviceTypes: string[],
  ranges: string[],
) {
  let tagFilters: UniqueLabel[] = [];

  const connectivityTags = connectivityTypes?.reduce(
    (acc: UniqueLabel[], connectivityType) => {
      const filterTag = filtersUtils.getTag(
        connectivityLabel.value,
        connectivityType,
        t(`dtwins.filters.connectivity.${connectivityType}`),
      );
      if (filterTag) {
        acc.push(filterTag);
      }
      return acc;
    },
    [],
  );

  tagFilters = [...tagFilters, ...connectivityTags];

  if (deviceTypes?.length) {
    const deviceTags = deviceTypes?.reduce((acc: UniqueLabel[], deviceType) => {
      const option = props.typeOptions?.options?.find((currentOption) => {
        return currentOption.value.includes(deviceType);
      });
      const filterTag = filtersUtils.getTag(
        deviceTypeLabel.value,
        deviceType,
        option?.label,
        option?.disabled || ranges.length > 0,
      );
      const findTag = acc.find((tag) => {
        return tag.label === filterTag?.label;
      });
      if (filterTag && !findTag) {
        acc.push(filterTag);
      }
      return acc;
    }, []);

    tagFilters = [...tagFilters, ...deviceTags];
  }

  if (ranges?.length) {
    const rangeTags = ranges?.reduce((acc: UniqueLabel[], range: string) => {
      const deviceTypeOption = filterTypeOptions.value.find(
        (currentOption) =>
          getFilterDtwinTypeOptionValue(currentOption) ===
          getFilterDtwinTypeValueFromRangeOptionValue(range),
      );

      const rangeValue = getFilterDtwinRangeValueFromRangeOptionValue(range);

      const rangeOption = deviceTypeOption?.ranges?.find(
        (rangeOption) => rangeOption.value === rangeValue,
      );

      const filterTag = filtersUtils.getTag(
        rangesLabel.value,
        range,
        rangeValue,
        rangeOption ? rangeOption.disabled : true, // disable the tag of the range if no type associated
      );
      if (filterTag) {
        acc.push(filterTag);
      }
      return acc;
    }, []);
    tagFilters = [...tagFilters, ...rangeTags];
  }

  return tagFilters;
}

function onAppliedTagDeleteClick(tag: string, uid?: string) {
  onDeleteSelectedTag(tag, uid);
  applyFilter();
}

function onDeleteSelectedTag(tag: string, uid?: string) {
  if (uid) {
    connectivityTypes.value = connectivityTypes.value?.filter(
      (connectivityType) => connectivityType !== uid,
    );

    types.value = types.value?.filter((deviceType) => {
      const foundOption = filterTypeOptions.value.find((option) => {
        return option.value.includes(uid) && option.value.includes(deviceType);
      });
      return deviceType !== uid && !foundOption?.value.includes(uid);
    });

    ranges.value = ranges.value?.filter((range) => range !== uid);
  }
}

async function fetchRanges() {
  const params = new Map();
  params.set(DISTINCT_VALUES_KEY, `(${DISTINCT_VALUES_RANGE})`);

  await fetchDataRange(0, 1, params);
}

watch(
  () => props.filters.get(Filters.DTWIN_MODEL_TYPE),
  () => {
    types.value = (props.filters.get(Filters.DTWIN_MODEL_TYPE)?.value ??
      []) as string[];
  },
  { deep: true, immediate: true },
);

watch(
  () => props.filters.get(Filters.DTWIN_RANGE),
  () => {
    // format the selected ranges values in order to include the device type prefix
    // if there is a filter on ranges, there is only one device type option
    if (props.typeOptions) {
      const deviceTypeOption = props.typeOptions.options.find(
        (dtwinTypeOption: DtwinTypeOption) =>
          serialize(types.value) === serialize(dtwinTypeOption.value),
      );

      ranges.value = (
        (props.filters.get(Filters.DTWIN_RANGE)?.value ?? []) as string[]
      ).map((range) =>
        getFilterDtwinRangeOptionValue(
          deviceTypeOption
            ? getFilterDtwinTypeOptionValue(deviceTypeOption)
            : "",
          range,
        ),
      );
    }
  },
  { deep: true, immediate: true },
);

onMounted(fetchRanges);
</script>

<template>
  <div class="relative mb-4">
    <filters-component
      :applied-tags="appliedFilterTags"
      :button-wrapper-class="['!pt-0']"
      :filter-sections="filterFormSections"
      :filters="filters"
      :filters-by-type="selectedFiltersByTypes"
      :selected-tags="selectedFilterTags"
      is-button-right
      @change="setFilter"
      @apply="applyFilter"
      @applied-filter-change="onAppliedFilterChange"
      @enter="emit('enter', $event)"
      @reset="clearFilter"
      @delete="onAppliedTagDeleteClick"
      @delete-selected="onDeleteSelectedTag"
    >
      <template #connectivity
        ><dtwin-filter-connectivity v-model="connectivityTypes" />
      </template>

      <template #device-types
        ><dtwin-filter-type
          v-model:types="types"
          v-model:ranges="ranges"
          :type-options="filterTypeOptions"
        />
      </template>
    </filters-component>
  </div>
</template>
